import { type SyntheticEvent, memo, useCallback } from 'react'

import type { VaultMediaItemDto } from '@api'
import { useUnit } from 'effector-react'

import { LoadingBar } from '@/components/ui/LoadingBar.tsx'
import { Button } from '@/components/ui/button.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { openDialog } from '@/models/dialogs.ts'
import { $showMediaItems } from '@/models/vault/categories.ts'
import {
  $canLoadMoreVaultMediaPages,
  $mediasToUse,
  $vaultMediasError,
  getNextVaultMediasPageFx,
  getVaultMediasFx,
  mediaSearchFx,
  refetchVaultMedias,
} from '@/models/vault/medias'
import { $mediaQueryParam } from '@/models/vault/medias/options.ts'
import { VaultFilterSection } from '@/modules/vault/ui/vault-filter-section.tsx'
import { VaultTypeSelection } from '@/modules/vault/ui/vault-type-selection.tsx'
import { classNames } from '@/utils/helpers.ts'

import { VaultMediaActions } from './vault-media-actions'
import { VaultMediaItem } from './vault-media-item'
import { VaultMediaPlaceholder } from './vault-media-placeholder'
import { VaultMediaSkeleton } from './vault-media-skeleton'
import { VaultMediaTitle } from './vault-media-title'

interface Props {
  isForSelection?: boolean
  showMediaActions?: boolean
  showTitle?: boolean
  showSettings?: boolean
  showTypeSelection?: boolean
  showAddMedia?: boolean
  hasDeleteMediaPermission?: boolean
  contentClassName?: string
  onClone?: (mediaItem: VaultMediaItemDto) => void
}

export const VaultMedia = memo(
  ({
    isForSelection = false,
    showMediaActions = false,
    showTitle = true,
    showSettings = true,
    showTypeSelection = true,
    showAddMedia = true,
    hasDeleteMediaPermission = false,
    contentClassName,
    onClone,
  }: Props) => {
    const showMediaItems = useUnit($showMediaItems)
    const mediaQuery = useUnit($mediaQueryParam)
    const canLoadMore = useUnit($canLoadMoreVaultMediaPages)
    const isLoadingMedia = useUnit(getVaultMediasFx.pending)
    const isSearchMediaLoading = useUnit(mediaSearchFx.pending)
    const mediasToUse = useUnit($mediasToUse)
    const error = useUnit($vaultMediasError)
    const widthCoefficient = isForSelection ? 4 : 3
    const handleMediaPreview = useCallback(
      (mediaId: number) => {
        openDialog({
          type: DIALOG_TYPE.VAULT_MEDIA_PREVIEW,
          mediaList: mediasToUse.map(({ id }) => id),
          mediaId,
        })
      },
      [mediasToUse],
    )
    const handleScroll = (event: SyntheticEvent<HTMLDivElement>) => {
      if (mediaQuery) {
        return
      }

      const target = event.target as HTMLDivElement
      const oneElementSize =
        ((target.clientWidth ?? 0) - (isForSelection ? 4 : 8)) /
        widthCoefficient
      const distanceToEnd =
        target.scrollHeight - target.clientHeight - target.scrollTop

      if (
        distanceToEnd <= oneElementSize * 2 &&
        !error &&
        !isLoadingMedia &&
        canLoadMore
      ) {
        getNextVaultMediasPageFx()
      }
    }

    const getVaultMediaContent = () => {
      const showMediaSkeleton = isLoadingMedia || isSearchMediaLoading

      if (error) {
        return (
          <VaultMediaPlaceholder
            body={
              <div className="flex h-full w-full flex-col items-center justify-center text-center">
                <span>Unexpected error occurred</span>
                <Button
                  className="mt-3 w-1/2"
                  onClick={refetchVaultMedias}
                  disabled={isLoadingMedia}
                >
                  Retry
                </Button>
              </div>
            }
          />
        )
      }
      if (mediasToUse.length) {
        return mediasToUse.map((item) => (
          <div
            className="col-span-1 max-md:col-span-1"
            key={item.id}
          >
            <VaultMediaItem
              item={item}
              showSettings={showSettings}
              selectionEnabled
              onClone={onClone}
              onMediaPreview={handleMediaPreview}
            />
          </div>
        ))
      }
      if (showMediaSkeleton) {
        return <VaultMediaSkeleton isForSelection={isForSelection} />
      }
      if (mediaQuery) {
        return <VaultMediaPlaceholder body="No media found" />
      }

      return <VaultMediaPlaceholder body="This category is empty" />
    }

    return (
      <div
        className={classNames(
          'relative flex flex-col w-full lg:flex-col overflow-hidden flex-1 @container',
          {
            visible: isForSelection ? true : showMediaItems,
            'lg:flex': isForSelection ? false : !showMediaItems,
          },
        )}
      >
        {showTitle && <VaultMediaTitle showAddMedia={showAddMedia} />}
        {!mediaQuery && <VaultFilterSection />}
        {showTypeSelection && !mediaQuery && <VaultTypeSelection />}
        <div
          onScroll={handleScroll}
          className={classNames(
            'grid grid-cols-2 gap-1',
            '@lg:grid-cols-3',
            '@xl:grid-cols-4',
            'scrollbar-hide min-h-[50vh] flex-1 flex-wrap content-start items-start gap-1 overflow-scroll lg:min-h-[auto]',
            contentClassName,
          )}
        >
          {getVaultMediaContent()}
        </div>
        {isLoadingMedia && <LoadingBar rootClassName="absolute bottom-0" />}
        <VaultMediaActions
          hasDeleteMediaPermission={hasDeleteMediaPermission}
          showMediaActions={showMediaActions}
        />
      </div>
    )
  },
)

VaultMedia.displayName = 'VaultMedia'
